import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { productAPI } from '../../services/api';
import Sidebar from '../../components/Common/Sidebar';
import ActionMenu from '../shared/ActionMenu';
import {
  Plus, Pencil, Trash2, Eye, Search,
  ArrowUpDown, Package,
  CheckCircle2, XCircle, X, Loader2,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'react-toastify';

/* ── design tokens ── */
const C = {
  bg:          '#f4f4f9',
  surface:     '#FFFFFF',
  border:      '#e8e8f1',
  borderSoft:  '#f0f0f6',
  ink:         '#14142b',
  body:        '#4a4a6a',
  muted:       '#6e6e86',
  primary:     '#7c3aed',
  primarySoft: '#f1ebff',
  primaryTint: '#ddd6fe',
  success:     '#10B981',
  successSoft: '#D1FAE5',
  warning:     '#F59E0B',
  warnSoft:    '#FEF3C7',
  danger:      '#EF4444',
  dangerSoft:  '#FEE2E2',
  radius:      14,
  radiusSm:    8,
};

const fmtINR = (n) =>
  '₹ ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/* ── helpers ── */
const inputSt = (focused) => ({
  width: '100%', padding: '9px 12px', fontSize: 13, color: C.ink,
  background: C.bg, border: `1.5px solid ${focused ? C.primary : C.border}`,
  borderRadius: C.radiusSm, outline: 'none', boxSizing: 'border-box',
});

const filterSel = {
  padding: '8px 12px', fontSize: 12, color: C.ink,
  background: C.bg, border: `1px solid ${C.border}`,
  borderRadius: C.radiusSm, outline: 'none', height: 36,
};

const TH = ['#', 'Name', 'Description', 'Amount', 'Currency', 'Type', 'Status', 'Created', 'Actions'];

const EMPTY_FORM = {
  name: '', description: '', amount: '', currency: 'INR', pricing_type: 'one_time', status: 'active',
};

const UserProductCatalogue = () => {
  const { user }  = useAuth();
  const [loading,           setLoading]           = useState(true);
  const [products,          setProducts]          = useState([]);
  const [showModal,         setShowModal]         = useState(false);
  const [showDetailModal,   setShowDetailModal]   = useState(false);
  const [selectedProduct,   setSelectedProduct]   = useState(null);
  const [editingProduct,    setEditingProduct]    = useState(null);
  const [submitting,        setSubmitting]        = useState(false);
  const [focused,           setFocused]           = useState(null);
  const [searchTerm,        setSearchTerm]        = useState('');
  const [statusFilter,      setStatusFilter]      = useState('');
  const [sortBy,            setSortBy]            = useState('created_at');
  const [sortOrder,         setSortOrder]         = useState('desc');
  const [pagination,        setPagination]        = useState({ page: 1, limit: 15, total: 0, totalPages: 1 });
  const [formData,          setFormData]          = useState(EMPTY_FORM);

  useEffect(() => { fetchProducts(); }, [searchTerm, statusFilter, sortBy, sortOrder, pagination.page]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await productAPI.getProducts({
        search: searchTerm, status: statusFilter,
        sort_by: sortBy, sort_order: sortOrder,
        per_page: pagination.limit, page: pagination.page,
      });
      if (res.data.data?.products) {
        setProducts(res.data.data.products.data || res.data.data.products || []);
        if (res.data.data.products.total) {
          setPagination(p => ({
            ...p,
            total: res.data.data.products.total,
            totalPages: res.data.data.products.last_page,
          }));
        }
      } else {
        setProducts(res.data.data || []);
      }
    } catch {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    if (name === 'amount') {
      if (value === '' || /^\d*\.?\d*$/.test(value)) setFormData(p => ({ ...p, [name]: value }));
    } else {
      setFormData(p => ({ ...p, [name]: value }));
    }
  };

  const resetForm = () => { setFormData(EMPTY_FORM); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim())                       { toast.error('Product name is required'); return; }
    if (!formData.amount || parseFloat(formData.amount) <= 0) { toast.error('Enter a valid amount');     return; }
    setSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        pricing_type: formData.pricing_type,
        status: formData.status,
      };
      if (editingProduct) {
        await productAPI.updateProduct(editingProduct.id, payload);
        toast.success('Product updated!');
      } else {
        await productAPI.createProduct(payload);
        toast.success('Product created!');
      }
      setShowModal(false);
      setEditingProduct(null);
      resetForm();
      await fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save product');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || '',
      amount: product.amount.toString(),
      currency: product.currency || 'INR',
      pricing_type: product.pricing_type || 'one_time',
      status: product.status || 'active',
    });
    setShowModal(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    try {
      await productAPI.deleteProduct(productId);
      toast.success('Product deleted!');
      await fetchProducts();
    } catch {
      toast.error('Failed to delete product');
    }
  };

  const closeModal = () => { setShowModal(false); setEditingProduct(null); resetForm(); };

  /* ── type badge ── */
  const TypeBadge = ({ type }) => (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20,
      background: type === 'one_time' ? C.primarySoft : C.warnSoft,
      color:      type === 'one_time' ? C.primary      : '#92400E',
    }}>
      {type === 'one_time' ? 'One-time' : 'Recurring'}
    </span>
  );

  /* ── status badge ── */
  const StatusBadge = ({ status }) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20,
      background: status === 'active' ? C.successSoft : C.borderSoft,
      color:      status === 'active' ? '#065F46'      : C.muted,
    }}>
      {status === 'active'
        ? <CheckCircle2 size={10} strokeWidth={2.5} />
        : <XCircle      size={10} strokeWidth={2.5} />
      }
      {(status || 'inactive').charAt(0).toUpperCase() + (status || 'inactive').slice(1)}
    </span>
  );


  return (
    <div className="dashboard-layout">
      <Sidebar role="user" />

      <div className="dashboard-content" style={{ background: C.bg, minHeight: '100vh', padding: '28px 28px 40px' }}>

        {/* ── page header ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: C.ink, margin: 0 }}>Product Catalogue</h2>
            <p  style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>Manage your payment products</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '9px 18px', borderRadius: C.radiusSm,
              background: C.primary, color: '#fff',
              fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer',
            }}
          >
            <Plus size={14} strokeWidth={2.5} /> Add Product
          </button>
        </div>

        {/* ── filter bar ── */}
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: C.radius, padding: '14px 18px', marginBottom: 18,
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          boxShadow: '0 1px 3px rgba(20,20,43,.04)',
        }}>
          {/* search */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 220px',
            background: C.bg, border: `1px solid ${C.border}`,
            borderRadius: C.radiusSm, padding: '0 12px', height: 36,
          }}>
            <Search size={13} color={C.muted} strokeWidth={2} />
            <input
              type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search products…"
              style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontSize: 13, color: C.ink }}
            />
          </div>

          {/* status filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {[{ label: 'All', val: '' }, { label: 'Active', val: 'active' }, { label: 'Inactive', val: 'inactive' }].map(t => (
              <button key={t.val} onClick={() => setStatusFilter(t.val)}
                style={{ height: 32, padding: '0 12px', borderRadius: 999, border: `1.5px solid ${statusFilter === t.val ? C.primary : C.border}`, background: statusFilter === t.val ? C.primary : 'transparent', color: statusFilter === t.val ? '#fff' : C.muted, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s' }}>
                {t.label}
              </button>
            ))}
          </div>

          {/* sort */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <ArrowUpDown size={12} color={C.muted} strokeWidth={2} />
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={e => { const [f, o] = e.target.value.split('-'); setSortBy(f); setSortOrder(o); }}
              style={filterSel}
            >
              <option value="created_at-desc">Newest First</option>
              <option value="created_at-asc">Oldest First</option>
              <option value="name-asc">Name A–Z</option>
              <option value="amount-desc">Price High–Low</option>
              <option value="amount-asc">Price Low–High</option>
            </select>
          </div>
        </div>

        {/* ── products table ── */}
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: C.radius, padding: '20px 24px',
          boxShadow: '0 1px 3px rgba(20,20,43,.04)',
        }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
              <Loader2 size={30} color={C.primary} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${C.borderSoft}` }}>
                      {TH.map(h => (
                        <th key={h} style={{
                          padding: '9px 14px', textAlign: 'left',
                          fontSize: 11, fontWeight: 700, color: C.muted,
                          textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {products.length > 0 ? products.map((p, idx) => (
                      <tr key={p.id}
                        style={{ borderBottom: `1px solid ${C.borderSoft}`, transition: 'background .1s' }}
                        onMouseEnter={e => e.currentTarget.style.background = C.bg}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '12px 14px', color: C.muted, fontSize: 12 }}>
                          {((pagination.page - 1) * pagination.limit) + idx + 1}
                        </td>
                        <td style={{ padding: '12px 14px', fontWeight: 700, color: C.ink, maxWidth: 160 }}>
                          <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {p.name}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', color: C.muted, fontSize: 12, maxWidth: 200 }}>
                          <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {p.description || '—'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', fontWeight: 800, color: C.ink, whiteSpace: 'nowrap' }}>
                          {fmtINR(p.amount)}
                        </td>
                        <td style={{ padding: '12px 14px', color: C.body, fontWeight: 600 }}>
                          {p.currency || 'INR'}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <TypeBadge type={p.pricing_type || 'one_time'} />
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <StatusBadge status={p.status || 'active'} />
                        </td>
                        <td style={{ padding: '12px 14px', color: C.muted, fontSize: 12, whiteSpace: 'nowrap' }}>
                          {fmtDate(p.created_at)}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <ActionMenu items={[
                            { label: 'View Details', Icon: Eye,    color: C.primary, bg: C.primarySoft, onClick: () => { setSelectedProduct(p); setShowDetailModal(true); } },
                            { label: 'Edit Product', Icon: Pencil, color: '#92400E', bg: C.warnSoft,    onClick: () => handleEdit(p) },
                            { divider: true },
                            { label: 'Delete',       Icon: Trash2, color: C.danger,  bg: C.dangerSoft,  onClick: () => handleDelete(p.id) },
                          ]} />
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={TH.length} style={{ padding: '60px 0', textAlign: 'center' }}>
                          <Package size={36} color={C.border} strokeWidth={1} />
                          <p style={{ color: C.muted, fontSize: 14, marginTop: 12 }}>No products found</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* pagination */}
              {pagination.totalPages > 1 && (
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.borderSoft}`, flexWrap: 'wrap', gap: 10,
                }}>
                  <span style={{ fontSize: 12, color: C.muted }}>
                    Showing {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                  </span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1}
                      style={{
                        width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`,
                        background: C.surface, cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                        color: pagination.page === 1 ? C.muted : C.body,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                      <ChevronLeft size={14} />
                    </button>
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => i + 1).map(pg => (
                      <button key={pg}
                        onClick={() => setPagination(p => ({ ...p, page: pg }))}
                        style={{
                          width: 32, height: 32, borderRadius: 8, fontSize: 13,
                          border: `1px solid ${pg === pagination.page ? C.primary : C.border}`,
                          background: pg === pagination.page ? C.primary : C.surface,
                          color: pg === pagination.page ? '#fff' : C.body,
                          fontWeight: pg === pagination.page ? 700 : 400,
                          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                        {pg}
                      </button>
                    ))}
                    <button
                      onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page === pagination.totalPages}
                      style={{
                        width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`,
                        background: C.surface, cursor: pagination.page === pagination.totalPages ? 'not-allowed' : 'pointer',
                        color: pagination.page === pagination.totalPages ? C.muted : C.body,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ══════════════ ADD / EDIT MODAL ══════════════ */}
      {showModal && (
        <div onClick={() => !submitting && closeModal()}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(20,20,43,.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, backdropFilter: 'blur(3px)',
          }}
        >
          <div onClick={e => e.stopPropagation()}
            style={{
              background: C.surface, borderRadius: C.radius,
              width: '100%', maxWidth: 500, padding: '28px 28px 24px',
              boxShadow: '0 20px 60px rgba(20,20,43,.18)',
              maxHeight: '90vh', overflowY: 'auto',
            }}
          >
            {/* header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: C.primarySoft, color: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {editingProduct ? <Pencil size={16} strokeWidth={2} /> : <Plus size={16} strokeWidth={2.5} />}
                </div>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.ink }}>
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </p>
              </div>
              <button onClick={() => !submitting && closeModal()}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, display: 'flex' }}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                {/* Name */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: C.body, marginBottom: 5, display: 'block' }}>
                    Product Name <span style={{ color: C.danger }}>*</span>
                  </label>
                  <input type="text" name="name" value={formData.name} onChange={handleInput}
                    onFocus={() => setFocused('name')} onBlur={() => setFocused(null)}
                    placeholder="Enter product name" required
                    style={inputSt(focused === 'name')} />
                </div>

                {/* Description */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: C.body, marginBottom: 5, display: 'block' }}>Description</label>
                  <textarea name="description" value={formData.description} onChange={handleInput}
                    onFocus={() => setFocused('desc')} onBlur={() => setFocused(null)}
                    placeholder="Optional description" rows={2}
                    style={{ ...inputSt(focused === 'desc'), resize: 'vertical', minHeight: 64 }} />
                </div>

                {/* Amount + Currency */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: C.body, marginBottom: 5, display: 'block' }}>
                      Amount <span style={{ color: C.danger }}>*</span>
                    </label>
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      border: `1.5px solid ${focused === 'amount' ? C.primary : C.border}`,
                      borderRadius: C.radiusSm, background: C.bg, overflow: 'hidden',
                    }}>
                      <span style={{ padding: '0 10px', fontSize: 14, fontWeight: 700, color: C.muted }}>₹</span>
                      <input type="text" name="amount" value={formData.amount} onChange={handleInput}
                        onFocus={() => setFocused('amount')} onBlur={() => setFocused(null)}
                        placeholder="0.00" required
                        style={{ flex: 1, padding: '9px 10px 9px 0', fontSize: 13, fontWeight: 700, color: C.ink, background: 'transparent', border: 'none', outline: 'none' }} />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: C.body, marginBottom: 5, display: 'block' }}>Currency</label>
                    <select name="currency" value={formData.currency} onChange={handleInput}
                      style={{ ...inputSt(false), height: 38 }}>
                      <option value="INR">INR</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>

                {/* Type + Status */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: C.body, marginBottom: 5, display: 'block' }}>Type</label>
                    <select name="pricing_type" value={formData.pricing_type} onChange={handleInput}
                      style={{ ...inputSt(false), height: 38 }}>
                      <option value="one_time">One-time</option>
                      <option value="recurring">Recurring</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: C.body, marginBottom: 5, display: 'block' }}>Status</label>
                    <select name="status" value={formData.status} onChange={handleInput}
                      style={{ ...inputSt(false), height: 38 }}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
                <button type="button" disabled={submitting} onClick={closeModal}
                  style={{ flex: 1, padding: '10px 0', borderRadius: C.radiusSm, background: C.bg, color: C.body, border: `1px solid ${C.border}`, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" disabled={submitting}
                  style={{
                    flex: 1, padding: '10px 0', borderRadius: C.radiusSm,
                    background: submitting ? C.muted : C.primary, color: '#fff', border: 'none',
                    fontWeight: 700, fontSize: 13, cursor: submitting ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  }}>
                  {submitting
                    ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</>
                    : editingProduct ? 'Update Product' : 'Create Product'
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════ DETAIL MODAL ══════════════ */}
      {showDetailModal && selectedProduct && (
        <div onClick={() => setShowDetailModal(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(20,20,43,.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, backdropFilter: 'blur(3px)',
          }}
        >
          <div onClick={e => e.stopPropagation()}
            style={{
              background: C.surface, borderRadius: C.radius,
              width: '100%', maxWidth: 440, padding: '28px 28px 24px',
              boxShadow: '0 20px 60px rgba(20,20,43,.18)',
            }}
          >
            {/* header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: C.primarySoft, color: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Eye size={16} strokeWidth={1.8} />
                </div>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.ink }}>Product Details</p>
              </div>
              <button onClick={() => setShowDetailModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, display: 'flex' }}>
                <X size={18} />
              </button>
            </div>

            {/* content */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { label: 'Product Name',  value: selectedProduct.name },
                { label: 'Description',   value: selectedProduct.description || '—' },
              ].map(({ label, value }) => (
                <div key={label} style={{ padding: '10px 0', borderBottom: `1px solid ${C.borderSoft}` }}>
                  <p style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', margin: 0 }}>{label}</p>
                  <p style={{ fontSize: 14, color: C.ink, fontWeight: 500, margin: '4px 0 0' }}>{value}</p>
                </div>
              ))}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div style={{ padding: '10px 0', borderBottom: `1px solid ${C.borderSoft}` }}>
                  <p style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', margin: 0 }}>Amount</p>
                  <p style={{ fontSize: 18, fontWeight: 800, color: C.ink, margin: '4px 0 0' }}>{fmtINR(selectedProduct.amount)}</p>
                </div>
                <div style={{ padding: '10px 0', borderBottom: `1px solid ${C.borderSoft}` }}>
                  <p style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', margin: 0 }}>Currency</p>
                  <p style={{ fontSize: 14, color: C.ink, fontWeight: 600, margin: '4px 0 0' }}>{selectedProduct.currency || 'INR'}</p>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, paddingBottom: 10, borderBottom: `1px solid ${C.borderSoft}` }}>
                <div>
                  <p style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', margin: '0 0 6px' }}>Type</p>
                  <TypeBadge type={selectedProduct.pricing_type || 'one_time'} />
                </div>
                <div>
                  <p style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', margin: '0 0 6px' }}>Status</p>
                  <StatusBadge status={selectedProduct.status || 'active'} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div>
                  <p style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', margin: '0 0 4px' }}>Created</p>
                  <p style={{ fontSize: 12, color: C.body, margin: 0 }}>{fmtDate(selectedProduct.created_at)}</p>
                </div>
                <div>
                  <p style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', margin: '0 0 4px' }}>Product ID</p>
                  <code style={{ fontSize: 11, color: C.body }}>{selectedProduct.product_id || selectedProduct.id}</code>
                </div>
              </div>
            </div>

            {/* footer buttons */}
            <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
              <button onClick={() => { setShowDetailModal(false); handleEdit(selectedProduct); }}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: C.radiusSm,
                  background: C.primary, color: '#fff', border: 'none',
                  fontWeight: 700, fontSize: 13, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                }}>
                <Pencil size={13} strokeWidth={2} /> Edit Product
              </button>
              <button onClick={() => setShowDetailModal(false)}
                style={{ flex: 1, padding: '10px 0', borderRadius: C.radiusSm, background: C.bg, color: C.body, border: `1px solid ${C.border}`, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default UserProductCatalogue;
